import { Text, TextInput } from "@mantine/core"
import { IconCheck } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { Button } from "@tfg/ui"
import { ChangeEvent, useMemo, useState } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { useUpdateTranslationsMutation } from "./hooks"

type Props = {
  defaultValue?: string
  onSave: (newValue: string) => void
}

export function EditableCellWithTextInput({
  defaultValue = "",
  onSave,
}: Props) {
  const [inputValue, setInputValue] = useState(defaultValue)
  const [isChanged, setIsChanged] = useState(false)

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
    setIsChanged(event.target.value !== defaultValue)
  }

  const handleSave = () => {
    onSave(inputValue)
    setIsChanged(false)
  }

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <TextInput
        value={inputValue}
        onChange={handleInputChange}
        style={{ flex: 1, minWidth: "180px" }}
      />
      {isChanged && (
        <Button
          size="xs"
          onClick={handleSave}
          style={{
            marginLeft: "8px",
          }}
        >
          <IconCheck size={16} strokeWidth={2} />
        </Button>
      )}
    </div>
  )
}

const columnHelper = createColumnHelper<AdminResponses.Translation>()
export const useColumns = () => {
  const { mutate: updateTranslation } = useUpdateTranslationsMutation()

  return useMemo<ColumnDef<AdminResponses.Translation, any>[]>(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: ({ row: { original: translation } }) => (
          <Text>{translation.id}</Text>
        ),
      }),
      columnHelper.accessor("originalText", {
        header: "Key",
        cell: ({ row: { original: translation } }) => (
          <Text>{translation.originalText}</Text>
        ),
      }),
      columnHelper.accessor("EN", {
        header: "EN",
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const value = getValue()
          return (
            <EditableCellWithTextInput
              defaultValue={value}
              onSave={(newValue: string) => {
                updateTranslation({
                  id: row.original.id,
                  translatedLanguage: "EN",
                  translation: newValue,
                })
              }}
            />
          )
        },
      }),
      columnHelper.accessor("FR", {
        header: "FR",
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const value = getValue()
          return (
            <EditableCellWithTextInput
              defaultValue={value}
              onSave={(newValue: string) => {
                updateTranslation({
                  id: row.original.id,
                  translatedLanguage: "FR",
                  translation: newValue,
                })
              }}
            />
          )
        },
      }),
      columnHelper.accessor("ZH", {
        header: "ZH",
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const value = getValue()
          return (
            <EditableCellWithTextInput
              defaultValue={value}
              onSave={(newValue: string) => {
                updateTranslation({
                  id: row.original.id,
                  translatedLanguage: "ZH",
                  translation: newValue,
                })
              }}
            />
          )
        },
      }),
      columnHelper.accessor("PT", {
        header: "PT",
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const value = getValue()
          return (
            <EditableCellWithTextInput
              defaultValue={value}
              onSave={(newValue: string) => {
                updateTranslation({
                  id: row.original.id,
                  translatedLanguage: "PT",
                  translation: newValue,
                })
              }}
            />
          )
        },
      }),
      columnHelper.accessor("IT", {
        header: "IT",
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const value = getValue()
          return (
            <EditableCellWithTextInput
              defaultValue={value}
              onSave={(newValue: string) => {
                updateTranslation({
                  id: row.original.id,
                  translatedLanguage: "IT",
                  translation: newValue,
                })
              }}
            />
          )
        },
      }),
      columnHelper.accessor("ES", {
        header: "ES",
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const value = getValue()
          return (
            <EditableCellWithTextInput
              defaultValue={value}
              onSave={(newValue: string) => {
                updateTranslation({
                  id: row.original.id,
                  translatedLanguage: "ES",
                  translation: newValue,
                })
              }}
            />
          )
        },
      }),
      columnHelper.accessor("TR", {
        header: "TR",
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const value = getValue()
          return (
            <EditableCellWithTextInput
              defaultValue={value}
              onSave={(newValue: string) => {
                updateTranslation({
                  id: row.original.id,
                  translatedLanguage: "TR",
                  translation: newValue,
                })
              }}
            />
          )
        },
      }),
    ],
    [updateTranslation]
  )
}
